export interface WebBreadcrumbItem {
  label: string;
  href?: string;
}

interface WebBreadcrumbParams {
  pathname: string;
  tenantId?: string;
  siteId?: string;
  cameraKey?: string;
  userId?: string;
}

export function getWebBreadcrumbs({
  pathname,
  tenantId,
  siteId,
  cameraKey,
  userId,
}: WebBreadcrumbParams): WebBreadcrumbItem[] {
  const items: WebBreadcrumbItem[] = [];
  const cameraHref =
    tenantId && siteId && cameraKey ? `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}` : undefined;
  const alarmSettingsHref =
    tenantId && siteId && cameraKey ? `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarm` : undefined;

  if (pathname === "/overview" || pathname === "/" || pathname.includes("cameras-list")) {
    return [{ label: "All cameras" }];
  }

  if (pathname === "/activity") {
    return [{ label: "Activity" }];
  }

  if (pathname.endsWith("/alarm-settings") && !pathname.includes("/cameras/")) {
    return [{ label: "Alarm settings" }];
  }

  if (pathname.includes("/alarm-settings/")) {
    const siteAlarmSettingsHref = tenantId ? `/tenants/${tenantId}/alarm-settings` : "/alarm-settings";
    const manageRoutinesHref = "/alarm-settings/manage-routines";

    let pageLabel = "";
    if (pathname.includes("/manage-routines")) pageLabel = "Manage routines";
    else if (pathname.includes("/edit-routine")) pageLabel = "Edit routine";
    else if (pathname.includes("/change-routine")) pageLabel = "Change routine";
    else if (pathname.includes("/manage-exceptions")) pageLabel = "Manage exceptions";
    else if (pathname.includes("/create-exception")) pageLabel = "Create exception";
    else if (pathname.includes("/edit-exception")) pageLabel = "Edit exception";
    else if (pathname.includes("/manage-alarm-response")) pageLabel = "Alarm response";
    else if (pathname.includes("/manage-notifications")) pageLabel = "Notifications";

    if (pathname.includes("/edit-routine")) {
      return [
        { label: "Alarm settings", href: siteAlarmSettingsHref },
        { label: "Manage routines", href: manageRoutinesHref },
        { label: pageLabel },
      ];
    }

    return [{ label: "Alarm settings", href: siteAlarmSettingsHref }, { label: pageLabel }];
  }

  if (pathname.includes("/cameras/") && pathname.endsWith("/clips") && cameraKey) {
    return [
      { label: "All cameras", href: "/overview" },
      { label: decodeURIComponent(cameraKey), href: cameraHref },
      { label: "Activity alerts" },
    ];
  }

  if (pathname.includes("/cameras/") && pathname.includes("/alarms/") && cameraKey) {
    let currentPageLabel = "Alarm Settings";
    if (pathname.includes("/alarms/routine")) currentPageLabel = "Change Routine";
    else if (pathname.includes("/alarms/customize-routine")) currentPageLabel = "Customize Routine";
    else if (pathname.includes("/alarms/response")) currentPageLabel = "Change Response";
    else if (pathname.includes("/alarms/timezone")) currentPageLabel = "Timezone";
    else if (pathname.includes("/alarms/applied-schedule")) currentPageLabel = "Applied Schedule";
    else if (pathname.includes("/alarms/exceptions/")) {
      currentPageLabel = pathname.endsWith("/new") || pathname.endsWith("/create") ? "Add Exception" : "Edit Exception";
    }

    return [
      { label: "All cameras", href: "/overview" },
      { label: decodeURIComponent(cameraKey), href: cameraHref },
      { label: "Alarm Settings", href: alarmSettingsHref },
      { label: currentPageLabel },
    ];
  }

  if (pathname.includes("/cameras/") && pathname.includes("/alarm") && cameraKey) {
    return [
      { label: "All cameras", href: "/overview" },
      { label: decodeURIComponent(cameraKey), href: cameraHref },
      { label: "Alarm Settings" },
    ];
  }

  if (pathname.includes("/cameras/") && cameraKey) {
    return [{ label: "All cameras", href: "/overview" }, { label: decodeURIComponent(cameraKey) }];
  }

  if (pathname === "/settings/site-access/camera-access/assign-sites") {
    return [
      { label: "All cameras", href: "/overview" },
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
      {
        label: "User profile",
        href: userId ? `/settings/site-access/camera-access/${userId}` : undefined,
      },
      { label: "Assign sites" },
    ];
  }

  if (pathname.startsWith("/settings/site-access/camera-access/")) {
    const cameraAccessItems: WebBreadcrumbItem[] = [
      { label: "All cameras", href: "/overview" },
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
    ];
    if (siteId) {
      cameraAccessItems.push({ label: decodeURIComponent(siteId), href: `/settings/site-access/${siteId}` });
    }
    cameraAccessItems.push({ label: "User profile" });
    return cameraAccessItems;
  }

  if (pathname.startsWith("/settings/site-access/add-users/") && siteId) {
    return [
      { label: "All cameras", href: "/overview" },
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
      { label: decodeURIComponent(siteId), href: `/settings/site-access/${siteId}` },
      { label: "Add users" },
    ];
  }

  if (pathname.startsWith("/settings/site-access/") && siteId) {
    return [
      { label: "All cameras", href: "/overview" },
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions", href: "/settings/users-and-permissions" },
      { label: decodeURIComponent(siteId) },
    ];
  }

  if (siteId) {
    return [{ label: "All sites", href: "/overview" }, { label: `Site ${siteId}` }];
  }

  if (pathname === "/settings") {
    return [{ label: "All cameras", href: "/overview" }, { label: "Settings" }];
  }

  if (pathname === "/settings/users-and-permissions") {
    return [
      { label: "All cameras", href: "/overview" },
      { label: "Settings", href: "/settings" },
      { label: "Users and Permissions" },
    ];
  }

  items.push({ label: "All cameras" });
  return items;
}
